import { get, set } from 'idb-keyval';
import { supabase } from '../services/supabase';

export interface Mutation {
  id: string;
  type: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  payload: Record<string, any>;
  timestamp: number;
  retryCount: number;
  lastFailedAt?: number;
}

const QUEUE_KEY = 'offline_queue';
const MUTATION_TTL_MS = 72 * 60 * 60 * 1000; // 72 saat
const MAX_RETRY_COUNT = 10;
const BASE_BACKOFF_MS = 1000;
const MAX_BACKOFF_MS = 5 * 60 * 1000; // 5 dakika

// Çift flush race condition'ını önleyen mutex
let isFlushing = false;

// ── Sync hata eventi — UI katmanına bildir ──────────────────────────────────
export type SyncErrorEvent = { pendingCount: number; lastError: string };
type SyncErrorListener = (e: SyncErrorEvent) => void;
const errorListeners: SyncErrorListener[] = [];
export function onSyncError(fn: SyncErrorListener) { errorListeners.push(fn); return () => { const i = errorListeners.indexOf(fn); if (i >= 0) errorListeners.splice(i, 1); }; }
function emitSyncError(e: SyncErrorEvent) { errorListeners.forEach(fn => fn(e)); }

// ── Kullanıcı tetiklemeli retry ─────────────────────────────────────────────
export async function retrySync(): Promise<void> { isFlushing = false; await flushQueue(); }

function backoffMs(retryCount: number): number {
  return Math.min(BASE_BACKOFF_MS * Math.pow(2, retryCount), MAX_BACKOFF_MS);
}

function isReadyToRetry(mutation: Mutation): boolean {
  if (!mutation.lastFailedAt) return true;
  return Date.now() - mutation.lastFailedAt >= backoffMs(mutation.retryCount);
}

function isExpired(mutation: Mutation): boolean {
  return Date.now() - mutation.timestamp > MUTATION_TTL_MS;
}

export async function enqueueMutation(mutation: Omit<Mutation, 'id' | 'timestamp' | 'retryCount'>): Promise<void> {
  const m: Mutation = {
    ...mutation,
    id: `mut_${Date.now()}_${crypto.randomUUID().slice(0, 8)}`,
    timestamp: Date.now(),
    retryCount: 0,
  };
  const queue = (await get<Mutation[]>(QUEUE_KEY)) || [];
  queue.push(m);
  await set(QUEUE_KEY, queue);

  if (navigator.onLine) {
    flushQueue().catch(err => console.error('[SyncEngine] enqueueMutation flush failed:', err));
  }
}

export async function flushQueue() {
  if (isFlushing || !navigator.onLine || !supabase) return;
  isFlushing = true;

  try {
    const queue = (await get<Mutation[]>(QUEUE_KEY)) || [];
    if (queue.length === 0) return;

    const remaining: Mutation[] = [];

    for (const m of queue) {
      // 72 saati geçmiş mutasyonları sessizce düşür
      if (isExpired(m)) continue;

      // Max retry aşılmışsa düşür
      if (m.retryCount >= MAX_RETRY_COUNT) continue;

      // Backoff süresi dolmamışsa sonraki tura bırak
      if (!isReadyToRetry(m)) {
        remaining.push(m);
        continue;
      }

      try {
        if (m.type === 'saveDeviceEvent') {
          const { error } = await supabase.from('device_events').upsert(m.payload, { onConflict: 'id' });
          if (error) throw error;
        } else if (m.type === 'saveAlert') {
          const { error } = await supabase.from('alerts').upsert(m.payload, { onConflict: 'id' });
          if (error) throw error;
        } else if (m.type === 'saveGrowStore') {
          // Grow verisi — gmc_store tablosuna yaz (key-value)
          const { error } = await supabase.from('gmc_store').upsert(
            { key: m.payload.key, value: m.payload.value, updated_at: new Date().toISOString() },
            { onConflict: 'key' }
          );
          if (error) throw error;
        }
        // Başarılı — remaining'e ekleme, queue'dan düşür
      } catch (err) {
        console.error('[SyncEngine] Mutation sync başarısız:', m.id, err);
        remaining.push({
          ...m,
          retryCount: m.retryCount + 1,
          lastFailedAt: Date.now(),
        });
      }
    }

    await set(QUEUE_KEY, remaining);
    if (remaining.length > 0) {
      emitSyncError({ pendingCount: remaining.length, lastError: 'Bazı değişiklikler senkronize edilemedi.' });
    }
  } finally {
    isFlushing = false;
  }
}

export async function getQueueDepth(): Promise<number> {
  const queue = (await get<Mutation[]>(QUEUE_KEY)) || [];
  return queue.filter(m => !isExpired(m) && m.retryCount < MAX_RETRY_COUNT).length;
}

export function setupSyncEngine() {
  window.addEventListener('online', () => {
    flushQueue();
  });

  // Periyodik flush — jitter ile thundering herd önle
  const jitter = Math.floor(Math.random() * 10000);
  setInterval(() => {
    if (!navigator.onLine) return;
    flushQueue();
  }, 60000 + jitter);
}
